import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Image,
  Text,
  Button,
  Heading,
  Spinner,
  Tooltip,
  useColorModeValue,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  HiEye,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi";

import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import ServicePanel from "./ServicePanel";
import FileEditorView from "./FileEditorView";

const Admin = () => {
  const { user } = useAuth();
  const isUserSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const navigate = useNavigate();

  const [topicsTable, setTopicsTable] = useState([]);

  const [permissionDefinitions, setPermissionDefinitions] = useState([]);
  const [permissionDefinitionsLoading, setPermissionDefinitionsLoading] = useState(true);

  const [permissionRules, setPermissionRules] = useState([]);

  const [parties, setParties] = useState([]);
  const [partyLoading, setPartyLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);

  const cardBg = useColorModeValue("white", "gray.700");

  const handleOpenParty = (party) => {
    navigate(`/party/${party._id}`);
  };

  useEffect(() => {
    const fetchPermissionDefinitions = async () => {
      try {
        const res = await fetch("https://permissions.poc.mrc.ai/api/definitions");
        const data = await res.json();
        setPermissionDefinitions(data);
      } catch (err) {
        console.error("Failed to fetch permission definitions:", err);
      } finally {
        setPermissionDefinitionsLoading(false);
      }
    };

    const fetchPermissionRules = async () => {
      try {
        const res = await fetch("https://permissions.poc.mrc.ai/api/rules");
        const data = await res.json();
        setPermissionRules(data);
      } catch (err) {
        console.error("Failed to fetch permission rules:", err);
      }
    };

    const fetchParties = async () => {
      try {
        const res = await fetch("https://directory.poc.mrc.ai/api/parties");
        const data = await res.json();
        setParties(data);
      } catch (err) {
        console.error("Failed to fetch parties:", err);
      } finally {
        setPartyLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await fetch("https://auth.poc.mrc.ai/api/users");
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      } finally {
        setUserLoading(false);
      }
    };

    fetchPermissionDefinitions();
    fetchPermissionRules();
    fetchParties();
    fetchUsers();
  }, []);

  const handleDeleteTopic = async (topic) => {
    try {
      const res = await fetch(
        `https://pipes.mrc.ai/api/topics/${encodeURIComponent(topic)}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) throw new Error(`Failed to delete ${topic}`);
      setTopicsTable((prev) =>
        prev
          .map((group) => ({
            ...group,
            items: group.items.filter((t) => t !== topic),
          }))
          .filter((group) => group.items.length > 0)
      );
    } catch (err) {
      alert(`❌ ${err.message}`);
    }
  };

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Box flexShrink={0}>
        <Navbar />
      </Box>

      <Box
        position="relative"
        width="100%"
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={1} display="flex" flexDirection="column" height="100%">
          <Tabs
            orientation="horizontal"
            variant="line"
            colorScheme="linkedin"
            isLazy={true}
            lazyBehavior="unmount"
            display="flex"
            flexDirection="column"
            height="100%">
            <TabList>
              <Tab>Permissions</Tab>
              {isUserSuperAdmin && <Tab>Pipelines</Tab>}
              {isUserSuperAdmin && <Tab>Services</Tab>}
              {isUserSuperAdmin && <Tab>Server Files</Tab>}
              {isUserSuperAdmin && <Tab>Parties</Tab>}
              <Tab>Users</Tab>
            </TabList>
            <TabPanels flex="1" overflow="auto">
              {/* Permissions Tab */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  mb={2}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flexShrink={0}>
                  <HStack>
                    <Tooltip label="Add Permission">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        aria-label="Add Permission"
                        size="md"
                        onClick={() => navigate("/permissiondefinitions/new")}
                      />
                    </Tooltip>
                  </HStack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  {permissionDefinitionsLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {permissionDefinitions.map((permissionDefinition) => {
                        const rulesCount = permissionRules.filter(
                          (rule) => rule.permissionCode === permissionDefinition.code
                        ).length;
                        return (
                    <WrapItem key={permissionDefinition._id}>
                      <Box
                        bg={cardBg}
                        p={4}
                        rounded="lg"
                        shadow="md"
                        border="1px solid"
                        borderColor="gray.300"
                        minW="200px"
                        maxW="fit-content"
                        transition="all 0.2s ease"
                        _hover={{
                          shadow: "lg",
                          transform: "translateY(-4px)",
                        }}>
                        <HStack>
                          <Tooltip label="Edit Permission...">
                            <IconButton
                              icon={<HiEye />}
                              aria-label="Edit permission"
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/permissiondefinitions/${permissionDefinition._id}`,
                                  { state: { permissionDefinition } }
                                )
                              }
                            />
                          </Tooltip>
                          <Heading size="sm" mb={0} noOfLines={1}>
                            {permissionDefinition.code}
                          </Heading>
                        </HStack>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          {permissionDefinition.description}
                        </Text>
                        <Text fontSize="xs" color="blue.600" mt={1}>
                          {rulesCount} {rulesCount === 1 ? 'rule' : 'rules'}
                        </Text>
                      </Box>
                    </WrapItem>
                        );
                      })}
                  </Wrap>
                )}
              </Box>
            </TabPanel>

            {/* Pipelines Tab - isSuperAdmin only */}
            {isUserSuperAdmin && (
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={1}
                borderColor={"gray.500"}
                backgroundColor="whiteAlpha.800"
                flex="1"
                overflow="auto">
                <Button
                  colorScheme="linkedin"
                  onClick={async () => {
                    try {
                      const response = await fetch(
                        "https://pipes.mrc.ai/api/topics"
                      );
                      if (!response.ok)
                        throw new Error("Failed to fetch topics");

                      const data = await response.json();

                      const group = (label, match) => {
                        const items = data
                          .filter((topic) => topic.includes(`.${match}.`))
                          .sort();
                        return items.length > 0 ? { label, items } : null;
                      };

                      const others = data
                        .filter(
                          (topic) =>
                            !topic.includes(".signals.") &&
                            !topic.includes(".triggers.") &&
                            !topic.includes(".events.") &&
                            !topic.includes(".temporal.")
                        )
                        .sort();

                      const groups = [
                        group("SIGNALS", "signals"),
                        group("TRIGGERS", "triggers"),
                        group("EVENTS", "events"),
                        group("TEMPORAL", "temporal"),
                        others.length > 0
                          ? { label: "OTHER", items: others }
                          : null,
                      ].filter(Boolean);

                      setTopicsTable(groups);
                    } catch (err) {
                      setTopicsTable([
                        { label: "ERROR", items: [`❌ ${err.message}`] },
                      ]);
                    }
                  }}>
                  View Channels
                </Button>

                {topicsTable.length === 0 ? (
                  <Text fontSize="sm" color="gray.700" mt={4}>
                    No channels loaded
                  </Text>
                ) : (
                  <Table
                    size="sm"
                    mt={4}
                    variant="striped"
                    colorScheme="gray">
                    <Thead>
                      <Tr>
                        <Th>Group</Th>
                        <Th>Channel</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {topicsTable.map((group, groupIdx) =>
                        group.items.map((topic, topicIdx) => (
                          <Tr key={`${group.label}-${topicIdx}`}>
                            <Td>{topicIdx === 0 ? group.label : ""}</Td>
                            <Td>{topic}</Td>
                            <Td>
                              <IconButton
                                icon={<HiOutlineTrash />}
                                aria-label="Delete topic"
                                size="xs"
                                onClick={() => handleDeleteTopic(topic)}
                              />
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </TabPanel>
            )}

            {/* Services Tab - isSuperAdmin only */}
            {isUserSuperAdmin && (
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={1}
                borderColor={"gray.500"}
                backgroundColor="whiteAlpha.800"
                flex="1"
                overflow="auto">
                <ServicePanel />
              </Box>
            </TabPanel>
            )}

            {/* Server Files Tab - isSuperAdmin only */}
            {isUserSuperAdmin && (
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={1}
                borderColor={"gray.500"}
                backgroundColor="whiteAlpha.800"
                flex="1"
                overflow="auto">
                <FileEditorView />
              </Box>
            </TabPanel>
            )}

            {/* Parties Tab - isSuperAdmin only */}
            {isUserSuperAdmin && (
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                    mb={2}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800">
                    <HStack>
                      <Tooltip label="Add party">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          aria-label="Add party"
                          size="md"
                          onClick={() => navigate("/party/new")}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {partyLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {parties.map((party) => (
                      <WrapItem key={party._id}>
                        <Box
                          bg={cardBg}
                          p={4}
                          rounded="lg"
                          shadow="md"
                          border="1px solid"
                          borderColor="gray.300"
                          minW="200px"
                          maxW="fit-content"
                          transition="all 0.2s ease"
                          _hover={{
                            shadow: "lg",
                            transform: "translateY(-4px)",
                          }}>
                          <HStack>
                            <Tooltip label="Edit party details">
                              <IconButton
                                icon={<HiEye />}
                                aria-label="Edit party"
                                size="sm"
                                onClick={() => handleOpenParty(party)}
                              />
                            </Tooltip>
                            <Heading size="sm" mb={0} noOfLines={1}>
                              {party.name}
                            </Heading>
                          </HStack>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            {party.roleCode}
                          </Text>
                        </Box>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </Box>
            </TabPanel>
            )}

          {/* Users Tab */}
          <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={1}
              borderColor={"gray.500"}
              backgroundColor="whiteAlpha.800"
              flex="1"
              overflow="auto">
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={1}
                mb={2}
                borderColor={"gray.500"}
                backgroundColor="whiteAlpha.800">
                <HStack>
                  <Tooltip label="Add user">
                    <IconButton
                      icon={<HiOutlinePlus />}
                      aria-label="Add user"
                      size="md"
                      onClick={() => navigate("/users/new")}
                    />
                  </Tooltip>
                </HStack>
              </Box>

              {userLoading ? (
                <Spinner />
              ) : (
                <Wrap spacing={4}>
                  {users.map((user) => (
                  <WrapItem key={user._id}>
                    <Box
                      bg={cardBg}
                      p={4}
                      rounded="lg"
                      shadow="md"
                      border="1px solid"
                      borderColor="gray.300"
                      minW="200px"
                      maxW="fit-content"
                      transition="all 0.2s ease"
                      _hover={{
                        shadow: "lg",
                        transform: "translateY(-4px)",
                      }}>
                      <HStack>
                        <Tooltip label="Edit User...">
                          <IconButton
                            icon={<HiEye />}
                            aria-label="Open user"
                            size="sm"
                            onClick={() => navigate(`/users/${user._id}`)}
                          />
                        </Tooltip>
                        <Heading size="sm" mb={0} noOfLines={1}>
                          {user.firstName} {user.lastName}
                        </Heading>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        {user.email}
                      </Text>
                    </Box>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </Box>
</Box>
</Box>
  );
};

export default Admin;
